import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/Event';
import api from '@/lib/api';
import { useBranding } from '@/lib/branding';

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

// Public landing page for check-in confirmation links sent in automated
// messages. No login required — the token in the URL is the credential.
const ConfirmAppointmentPage = () => {
  const { token } = useParams();
  const { branding } = useBranding();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    api
      .post(`/public/appointment-confirm/${token}`)
      .then((res) => {
        if (!cancelled) setState({ status: 'ok', data: res.data });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            status: 'error',
            message: err.response?.data?.detail || 'This confirmation link is invalid or has expired.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper variant="outlined" sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={700} color="primary" sx={{ mb: 3 }}>
          {branding.app_name}
        </Typography>
        {state.status === 'loading' && <CircularProgress />}
        {state.status === 'ok' && (
          <>
            <CheckCircleIcon sx={{ fontSize: 44, color: 'success.main', mb: 1 }} />
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {state.data.already_confirmed ? 'Already confirmed' : 'You’re confirmed!'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {state.data.service_name}
              {state.data.start_time ? ` — ${fmt(state.data.start_time)}` : ''}
            </Typography>
            {state.data.calendar_url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventIcon />}
                href={state.data.calendar_url}
                sx={{ mt: 2.5 }}
                data-testid="add-to-calendar-button"
              >
                Add to calendar (.ics)
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              See you soon{state.data.member_name ? `, ${state.data.member_name.split(' ')[0]}` : ''}!
            </Typography>
          </>
        )}
        {state.status === 'error' && (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 44, color: 'error.main', mb: 1 }} />
            <Typography variant="body1">{state.message}</Typography>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ConfirmAppointmentPage;
